// packages/ui-primitives/src/section-semantics.js
//
// Single parser for a section name -> { type, number }. Two consumers depend
// on it: section colour (section-colors.matchSemantic) and guide-pack voice
// prompts (renderer/lib/guide-schedule). Keeping them on one dictionary is
// mandatory: two separate lists drift apart the first time a word is added.
//
// Rule order matters: more specific keys go first (pre-chorus before
// chorus), because matching is substring-based — "Pre-Chorus" contains
// "chorus".
//
// Substring matching also means false positives are possible on
// non-canonical names: "Tagline" matches `tag`, "Subverses" matches `verse`.
// This dictionary used to drive only colour, where a false positive meant a
// cosmetic wrong-coloured swatch. It now also picks the word the app speaks
// out loud for a guide cue — so the same false positive makes the app
// announce "Verse" for a section literally named "Subverses". Accepted for
// canonical-name use cases (Verse 1 / Chorus / Куплет / Pre-chorus); if that
// stops being acceptable, tighten the matching itself (e.g. word-boundary
// regex) here, not in one of the two consumers.

const SEMANTIC_RULES = [
  { type: 'pre-chorus',   keys: ['pre-chorus', 'prechorus', 'припев-проигрыш', 'предприпев'] },
  { type: 'intro',        keys: ['intro', 'вступление', 'интро'] },
  { type: 'verse',        keys: ['verse', 'куплет'] },
  { type: 'chorus',       keys: ['chorus', 'припев'] },
  { type: 'bridge',       keys: ['bridge', 'бридж', 'мост'] },
  { type: 'instrumental', keys: ['solo', 'instrumental', 'проигрыш', 'соло'] },
  // outro and ending are DIFFERENT types: the voice guide pack ships two
  // distinct words for them. Colour is the same for both (see
  // section-colors), but the spoken phrase differs.
  { type: 'ending',       keys: ['ending', 'финал', 'концовка'] },
  { type: 'outro',        keys: ['outro', 'аутро'] },
  { type: 'tag',          keys: ['tag', 'refrain', 'реприз', 'рефрен'] },
];

// Trailing number only. Applied to an already-trimmed string, so there is no
// trailing whitespace left to match — the number must be the last token.
const TRAILING_NUMBER = /\s(\d{1,2})$/;

export function classifySection(name) {
  if (typeof name !== 'string') return null;
  const trimmed = name.trim();
  if (!trimmed) return null;
  const norm = trimmed.toLowerCase();
  for (const rule of SEMANTIC_RULES) {
    if (rule.keys.some(k => norm.includes(k))) {
      const m = TRAILING_NUMBER.exec(trimmed);
      // 0 is not a valid section number, and treating it as "no number"
      // matters: a consumer checking `if (sem.number)` must not silently
      // treat "Verse 0" the same as "Verse 3". Anything below 1 collapses to
      // null instead of a falsy-but-present 0.
      const num = m ? Number(m[1]) : null;
      return { type: rule.type, number: num >= 1 ? num : null };
    }
  }
  return null;
}

export const SEMANTIC_TYPES = Object.freeze(SEMANTIC_RULES.map(r => r.type));

// Отображаемые имена для подсказки при вводе имени секции (Сергей 07.10):
// по умолчанию английский (то, что произносит гайд), кириллица → русский.
// Замок в тесте: каждое имя классифицируется обратно в свой тип — иначе
// подсказка вела бы к имени, на которое гайд молчит. Заморожена и каждая пара:
// Object.freeze на внешнем массиве не защищает вложенные [type, label].
const pair = (type, label) => Object.freeze([type, label]);
export const SECTION_LABELS = Object.freeze({
  en: Object.freeze([
    pair('intro', 'Intro'), pair('verse', 'Verse'), pair('pre-chorus', 'Pre-Chorus'),
    pair('chorus', 'Chorus'), pair('bridge', 'Bridge'), pair('instrumental', 'Instrumental'),
    pair('tag', 'Tag'), pair('ending', 'Ending'), pair('outro', 'Outro'),
  ]),
  ru: Object.freeze([
    pair('intro', 'Вступление'), pair('verse', 'Куплет'), pair('pre-chorus', 'Предприпев'),
    pair('chorus', 'Припев'), pair('bridge', 'Бридж'), pair('instrumental', 'Проигрыш'),
    pair('tag', 'Реприза'), pair('ending', 'Финал'), pair('outro', 'Аутро'),
  ]),
});

// Куплет нумеруется всегда (Verse 1, 2, …); остальные — только когда в песне
// уже есть секция того же типа.
const NUMBERED_ALWAYS = new Set(['verse']);

// Дефис и пробел равнозначны («pre c» и «pre-ch» находят «Pre-Chorus»).
const flatten = (str) => str.replace(/[-\s]+/g, ' ');

// Совпадение — по границе слова: запрос должен начинаться с начала имени
// либо сразу после пробела/дефиса. Голый includes() ломал русский список:
// «при» находил «Предприпев» (пред-ПРИ-пев), а набирающий «Припев» хочет
// только его. При этом «ch» по-прежнему находит «Pre-Chorus» (после дефиса).
// Возвращает ранг: 0 — с начала имени, 1 — после границы слова, -1 — нет.
// Ранг нужен для порядка: «ch» → «Chorus» раньше «Pre-Chorus».
function matchRank(text, q) {
  if (!q) return 0;
  const flat = flatten(text.toLowerCase());
  if (flat.startsWith(q)) return 0;
  for (let i = 0; i < flat.length - 1; i++) {
    if (flat[i] === ' ' && flat.startsWith(q, i + 1)) return 1;
  }
  return -1;
}

/**
 * Подсказки имён секции при вводе. Порядок — словарный, но совпадения с
 * начала имени идут раньше совпадений после пробела/дефиса («ch» → Chorus,
 * Pre-Chorus). Пустой/nullish запрос — весь список. Совпадение — по началу
 * слова, дефис ≈ пробел. Номер = максимальный существующий + 1 (имя без
 * номера считается первым).
 * @param {string|null|undefined} [query]
 * @param {string[]} [existingNames=[]]  имена ДРУГИХ секций песни — переименовываемую секцию сюда не передавать
 * @returns {Array<{type: string, text: string}>}
 */
export function suggestSectionNames(query, existingNames = []) {
  const q = flatten(String(query ?? '').trim().toLowerCase());
  // Язык — по первой набранной букве, не по языку интерфейса. Украинских слов в
  // словаре нет: украинский запрос получит русский список (по префиксу) или
  // ничего — принято.
  const lang = /^[а-яёіїєґ]/.test(q) ? 'ru' : 'en';
  // Следующий номер = максимальный существующий + 1, а не количество секций:
  // при Verse 1 + Verse 3 счёт по количеству предложил бы дубль «Verse 3».
  // Имя типа без номера («Chorus») считается первым. Номера ≥100 вне
  // двузначного хвоста classifySection — практически недостижимо, принято.
  const highest = {};
  for (const n of Array.isArray(existingNames) ? existingNames : []) {
    const sem = classifySection(n);
    if (sem) highest[sem.type] = Math.max(highest[sem.type] ?? 0, sem.number ?? 1);
  }
  const ranked = SECTION_LABELS[lang].map(([type, label]) => {
    const max = highest[type] ?? 0;
    const text = NUMBERED_ALWAYS.has(type) || max > 0 ? `${label} ${max + 1}` : label;
    return { type, text, rank: matchRank(text, q) };
  });
  // Стабильная сортировка (ES2019): внутри группы порядок словаря сохраняется.
  return ranked
    .filter(x => x.rank >= 0)
    .sort((x, y) => x.rank - y.rank)
    .map(({ type, text }) => ({ type, text }));
}
